import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card } from "@/components/ui/card";
import { ExternalLink, Building, MapPin } from "lucide-react";

interface RegistrationLink {
  name: string;
  description: string;
  url: string;
}

interface Section {
  title: string;
  icon: string;
  jurisdictions: { name: string; links: RegistrationLink[] }[];
}

const sections: Section[] = [
  {
    title: "Canada",
    icon: "🇨🇦",
    jurisdictions: [
      {
        name: "Federal (Canada-wide)",
        links: [
          {
            name: "Corporations Canada — Federal Incorporation",
            description: "Incorporate a federal corporation under the Canada Business Corporations Act (CBCA). A federal incorporation allows you to operate under your corporate name across all provinces and territories.",
            url: "https://ised-isde.canada.ca/site/corporations-canada/en/incorporating-federally",
          },
          {
            name: "Corporations Canada Online Filing Centre",
            description: "File articles of incorporation, annual returns, and other corporate documents online.",
            url: "https://ised-isde.canada.ca/cc/lgcy/fdrlCrpSrch.html?lang=eng",
          },
        ],
      },
      {
        name: "Ontario",
        links: [
          {
            name: "Ontario Business Registry",
            description: "Incorporate a business in Ontario, register business names, and file annual returns through the Ontario Business Registry.",
            url: "https://www.ontario.ca/page/ontario-business-registry",
          },
          {
            name: "ServiceOntario — Incorporating a Business",
            description: "Step-by-step guide to incorporating a business in Ontario under the Ontario Business Corporations Act.",
            url: "https://www.ontario.ca/page/incorporating-business-corporation",
          },
        ],
      },
      {
        name: "Quebec",
        links: [
          {
            name: "Registraire des entreprises du Québec",
            description: "Register or incorporate a business in Quebec through the Quebec Enterprise Registrar.",
            url: "https://www.registreentreprises.gouv.qc.ca/en/default.aspx",
          },
          {
            name: "Revenu Québec — Starting a Business",
            description: "Tax registration and obligations for new businesses in Quebec.",
            url: "https://www.revenuquebec.ca/en/businesses/starting-a-business/",
          },
        ],
      },
      {
        name: "British Columbia",
        links: [
          {
            name: "BC Registry Services",
            description: "Incorporate a company, register a business name, or form a society in British Columbia.",
            url: "https://www2.gov.bc.ca/gov/content/employment-business/business/managing-a-business/permits-licences/businesses-incorporated-702702702702702702702702702",
          },
          {
            name: "BC Online — Corporate Registry",
            description: "File incorporation documents and manage your BC company online.",
            url: "https://www.bcregistry.gov.bc.ca/",
          },
        ],
      },
    ],
  },
  {
    title: "United States",
    icon: "🇺🇸",
    jurisdictions: [
      {
        name: "Delaware",
        links: [
          {
            name: "Delaware Division of Corporations",
            description: "Delaware is the most popular state for incorporation in the US. Over 60% of Fortune 500 companies are incorporated here due to its business-friendly laws and Court of Chancery.",
            url: "https://corp.delaware.gov/",
          },
          {
            name: "Delaware — How to Form a New Business Entity",
            description: "Step-by-step instructions for forming an LLC, corporation, or partnership in Delaware.",
            url: "https://corp.delaware.gov/howtoform/",
          },
        ],
      },
      {
        name: "California",
        links: [
          {
            name: "California Secretary of State — Start a Business",
            description: "File articles of incorporation or organization and register your business in California.",
            url: "https://www.sos.ca.gov/business-programs/business-entities/starting-business",
          },
          {
            name: "CalGold — California Permits and Licenses",
            description: "Find permits, licenses, and registration requirements for your business in California.",
            url: "https://www.calgold.ca.gov/",
          },
        ],
      },
      {
        name: "Texas",
        links: [
          {
            name: "Texas Secretary of State — Business Formations",
            description: "File a certificate of formation to create an LLC, corporation, or other business entity in Texas.",
            url: "https://www.sos.state.tx.us/corp/forms_702702702702702702702702702702702702.shtml",
          },
          {
            name: "Texas Comptroller — Starting a Business",
            description: "Register for taxes, obtain permits, and understand your obligations as a Texas business.",
            url: "https://comptroller.texas.gov/taxes/starting-business.php",
          },
        ],
      },
      {
        name: "Florida",
        links: [
          {
            name: "Florida Division of Corporations — Sunbiz",
            description: "File articles of incorporation or organization online to form a business in Florida.",
            url: "https://dos.fl.gov/sunbiz/start-business/",
          },
          {
            name: "Florida Department of Revenue — New Business",
            description: "Register for Florida taxes and understand your sales tax obligations.",
            url: "https://floridarevenue.com/taxes/businesses/Pages/default.aspx",
          },
        ],
      },
      {
        name: "New York",
        links: [
          {
            name: "New York Department of State — Forming a Business",
            description: "File incorporation or organization documents to form a business entity in New York.",
            url: "https://dos.ny.gov/forming-business",
          },
          {
            name: "NY Business Express",
            description: "One-stop resource for starting, operating, and growing a business in New York State.",
            url: "https://businessexpress.ny.gov/",
          },
        ],
      },
    ],
  },
];

export default function CompanyRegistrations() {
  return (
    <>
      <Navbar />
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" aria-hidden="true" />
          <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
            <div className="mb-16">
              <div className="flex items-center gap-3 mb-6">
                <div className="h-px w-8 bg-primary" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">Founder Resources</span>
              </div>
              <h1 className="text-2xl sm:text-4xl md:text-6xl font-display font-bold mb-6" data-testid="text-company-reg-title">Company Registrations</h1>
              <p className="text-xl text-muted-foreground max-w-2xl">
                Official government links for incorporating a company in Canada and the United States.
              </p>
            </div>

            {sections.map((section) => (
              <div key={section.title} className="mb-16">
                <h2 className="text-2xl font-display font-bold mb-8 flex items-center gap-3">
                  <span className="text-3xl" aria-hidden="true">{section.icon}</span>
                  {section.title}
                </h2>

                <div className="space-y-8">
                  {section.jurisdictions.map((jurisdiction) => (
                    <div key={jurisdiction.name}>
                      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                        <MapPin size={16} className="text-primary" aria-hidden="true" />
                        {jurisdiction.name}
                      </h3>
                      <div className="space-y-3" role="list" aria-label={`${jurisdiction.name} registration links`}>
                        {jurisdiction.links.map((link) => (
                          <Card key={link.url} role="listitem" className="border-white/5 p-5 hover:border-primary/20 transition-all">
                            <a
                              href={link.url}
                              target="_blank"
                              rel="noreferrer"
                              className="flex items-start gap-4 group"
                              data-testid={`link-reg-${link.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/-+$/, "")}`}
                            >
                              <Building size={18} className="text-primary shrink-0 mt-1" aria-hidden="true" />
                              <div className="flex-1">
                                <h4 className="font-semibold group-hover:text-primary transition-colors flex items-center gap-2">
                                  {link.name}
                                  <ExternalLink size={14} className="text-muted-foreground" aria-hidden="true" />
                                  <span className="sr-only">(opens in new tab)</span>
                                </h4>
                                <p className="text-sm text-muted-foreground mt-1">{link.description}</p>
                              </div>
                            </a>
                          </Card>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
